/**
 * 시스템.
 *
 * 프로토타입의 "설정" 자리다. 다만 SignageOne 은 콘솔에서 바꿀 수 있는 서버 설정을
 * 두지 않는다 — 서비스 활성화·신원 발급자·저장소 같은 값은 배포 환경(환경변수)에서
 * 오고, 그것을 콘솔에서 바꾸면 배포와 실제 상태가 어긋난다.
 *
 * 그래서 여기는 **읽기 전용 진단 화면**이다. 통합이 안 될 때 "무엇이 어떻게 붙어
 * 있나"를 한 화면에서 확인하는 것이 목적이다.
 */
import { useRef, useState, type ReactNode } from 'react';
import { CheckCircle2, Download, Upload, XCircle } from 'lucide-react';
import type { AddressDbStatus, AddressDbSummary, Me, ReadyReport, SignageOneClient } from '../api/client.js';
import type { Transport } from '../api/transport.js';
import { useAction, useAsync, usePolling } from '../hooks.js';
import { ErrorNote, formatSeconds, Panel } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { CAPABILITY_LABEL, can } from '../capabilities.js';

const TRANSPORT_LABEL: Record<Transport['kind'], string> = {
  direct: '독립 웹 · 브라우저가 서버에 직접 호출',
  embed: 'iframe 임베드 · 부모 창을 통해 호출',
  'host-bridge': '호스트 브리지 · 호스트가 대신 호출',
};

/**
 * 이 서버를 "정상" 이라고 말해도 되는가 — **진단 화면의 기준.**
 *
 * `status` 는 오케스트레이터용(트래픽을 받아도 되나)이고, 저장소가 죽어도 `ok` 다.
 * 이 화면의 질문은 다르다 — "지금 무엇이 어떻게 붙어 있나" 이므로, 한 줄이라도 응답하지
 * 않으면 저하다. 화면에서 떼어 둔 것은 시험할 수 있게 하기 위해서다.
 */
export function healthOk(ready: ReadyReport): boolean {
  return ready.status === 'ok' && ready.checks.database === 'ok' && ready.checks.storage === 'ok';
}

export function SystemScreen({
  client,
  transport,
  me,
}: {
  client: SignageOneClient;
  transport: Transport;
  me: Me | null;
}): ReactNode {
  const ready = useAsync(() => client.ready(), [client]);
  const metrics = useAsync(() => client.metrics(), [client]);
  usePolling(metrics.reload, 15_000);

  const cache = metrics.data?.resolve_cache;

  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <Panel title="서버 상태">
        <div className="p-4">
          {/*
            **한 줄이라도 죽어 있으면 "정상" 이라고 말하지 않는다.**

            `status` 는 로드밸런서용 판정이라 저장소가 죽어도 `ok` 다(그 편이 옳다 —
            편성·하트비트는 계속 돌아간다). 그런데 이 화면은 "무엇이 어떻게 붙어 있나" 를
            보러 오는 자리다. 업로드가 500 으로 죽고 미디어가 안 나가는 동안 여기가
            "정상" 이라고 하면, 사람은 서버가 아니라 자기 파일을 의심한다.
          */}
          <Row
            label="상태"
            value={
              ready.data ? (
                <Status ok={healthOk(ready.data)}>
                  {healthOk(ready.data) ? '정상' : '저하됨'}
                </Status>
              ) : (
                <span className="hint">{ready.error ?? '확인 중…'}</span>
              )
            }
          />
          <Row
            label="데이터베이스"
            value={
              ready.data ? (
                <Status ok={ready.data.checks.database === 'ok'}>{ready.data.checks.database}</Status>
              ) : (
                <span className="hint">—</span>
              )
            }
          />
          <Row
            label="저장소"
            value={
              ready.data ? (
                <Status ok={ready.data.checks.storage === 'ok'}>
                  {ready.data.checks.storage === 'ok' ? 'ok' : '응답 없음 (업로드·미디어 중단)'}
                </Status>
              ) : (
                <span className="hint">—</span>
              )
            }
          />
          <Row
            label="서비스 활성화"
            value={
              ready.data ? (
                <Status ok={ready.data.checks.service_enabled}>
                  {ready.data.checks.service_enabled ? '켜짐' : '꺼짐'}
                </Status>
              ) : (
                <span className="hint">—</span>
              )
            }
          />
          <Row
            label="가동 시간"
            value={
              metrics.data ? (
                <span className="mono">{formatSeconds(metrics.data.uptime_sec)}</span>
              ) : (
                <span className="hint">—</span>
              )
            }
          />
          <Row
            label="메모리"
            value={
              metrics.data ? (
                <span className="mono">
                  RSS {metrics.data.memory.rss_mb}MB · 힙 {metrics.data.memory.heap_used_mb}MB
                </span>
              ) : (
                <span className="hint">—</span>
              )
            }
          />
        </div>
      </Panel>

      {/*
        연결·내 권한·편성 캐시는 진단할 때 보는 참고 정보라 접을 수 있게 둔다(펼친 채로 연다).
        접힌 머리에 한 줄 요약을 남겨 열지 않고도 큰 그림을 본다.
      */}
      <Panel title="연결" subtitle={`${transport.kind}${me ? ` · ${me.role}` : ''}`} collapsible>
        <div className="p-4">
          <Row
            label="전송 방식"
            value={
              <span>
                <span className="badge badge-info">{transport.kind}</span>
                <span className="hint">{TRANSPORT_LABEL[transport.kind]}</span>
              </span>
            }
          />
          <Row label="주체" value={me ? <span className="mono">{me.id}</span> : <span className="hint">—</span>} />
          <Row
            label="역할"
            value={me ? <span className="badge badge-brand">{me.role}</span> : <span className="hint">—</span>}
          />
          <Row
            label="테넌트"
            value={me ? <span className="mono">{me.tenant_id}</span> : <span className="hint">—</span>}
          />
          <Row
            label="신원 발급자"
            value={
              me?.issuer ? (
                <span className="mono">{me.issuer}</span>
              ) : (
                <span className="hint">없음 (서비스 토큰 연결)</span>
              )
            }
          />
          <Row
            label="허용 발급자"
            value={
              ready.data ? (
                ready.data.checks.identity_issuers.length > 0 ? (
                  <span className="mono">{ready.data.checks.identity_issuers.join(', ')}</span>
                ) : (
                  <span className="hint">등록된 호스트 발급자 없음</span>
                )
              ) : (
                <span className="hint">—</span>
              )
            }
          />
        </div>
      </Panel>

      <Panel
        title="내 권한"
        subtitle={
          me
            ? `${Object.keys(CAPABILITY_LABEL).filter((capability) => me.capabilities.includes(capability)).length} / ${Object.keys(CAPABILITY_LABEL).length}`
            : undefined
        }
        help="권한이 없는 기능은 화면에서 감춰집니다. 실제 허용 여부는 서버가 최종 판단합니다."
        collapsible
      >
        <div className="p-4">
          {me ? (
            <ul className="flex flex-wrap gap-1.5">
              {Object.keys(CAPABILITY_LABEL).map((capability) => {
                const granted = me.capabilities.includes(capability);
                return (
                  <li
                    key={capability}
                    className={`rounded-full border px-2.5 py-1 text-[12.5px] ${
                      granted
                        ? 'border-th-6/60 bg-th-2 text-[#3f6212]'
                        : 'border-slate-200 bg-slate-50 text-slate-400 line-through'
                    }`}
                  >
                    {CAPABILITY_LABEL[capability]}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="hint">확인 중…</p>
          )}
        </div>
      </Panel>

      {me && can(me, 'system') ? <BackupPanel client={client} /> : null}
      {me && can(me, 'system') ? <AddressDbPanel client={client} /> : null}

      <Panel
        title="편성 캐시"
        subtitle={cache ? `적중률 ${(cache.hitRate * 100).toFixed(1)}%` : undefined}
        help="편성 결과는 내용이 바뀔 때만 다시 계산합니다. 실패가 계속 늘면 화면별 편성이 제각각이라 서버 부하가 커집니다."
        collapsible
      >
        <div className="p-4">
          <Row
            label="적중률"
            value={
              cache ? (
                <span className="mono">{(cache.hitRate * 100).toFixed(1)}%</span>
              ) : (
                <span className="hint">—</span>
              )
            }
          />
          <Row
            label="적중 / 실패"
            value={
              cache ? (
                <span className="mono">
                  {cache.hits} / {cache.misses}
                </span>
              ) : (
                <span className="hint">—</span>
              )
            }
          />
          <Row
            label="항목 수"
            value={cache ? <span className="mono">{cache.size}</span> : <span className="hint">—</span>}
          />
        </div>
      </Panel>
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="flex items-baseline gap-4 border-b border-slate-100 py-2 last:border-0">
      <span className="w-28 shrink-0 text-[12.5px] text-slate-500">{label}</span>
      <span className="min-w-0 flex-1 text-[13.5px]">{value}</span>
    </div>
  );
}

function Status({ ok, children }: { ok: boolean; children: ReactNode }): ReactNode {
  const Icon = ok ? CheckCircle2 : XCircle;
  return (
    <span className={`inline-flex items-center gap-1.5 ${ok ? 'text-emerald-600' : 'text-red-600'}`}>
      <Icon className="h-4 w-4" strokeWidth={2} />
      {children}
    </span>
  );
}

/**
 * 백업 파일 내려받기 — 관리자만.
 *
 * 서버 백업은 서버에 남는다. 서버째 잃으면 백업도 같이 잃으므로, 관리자가 브라우저로 한 벌 받아
 * **다른 곳에** 두게 한다. 서버 설정(.env)은 담지 않는다 — 저장소 비밀번호가 브라우저로 흐르지 않게.
 */
function BackupPanel({ client }: { client: SignageOneClient }): ReactNode {
  const [includeMedia, setIncludeMedia] = useState(true);
  const action = useAction();

  return (
    <Panel
      title="백업"
      help="편성·화면·계정·재생 기록과 콘텐츠 원본을 한 파일(.tar.gz)로 받습니다. 서버 설정(.env)과 로그인 세션은 제외되며, 복원은 scripts/restore-backup.mjs 로 합니다."
    >
      <div className="space-y-3 p-4">
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        <label className="flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={includeMedia} onChange={(event) => setIncludeMedia(event.target.checked)} />
          콘텐츠 원본(이미지·영상) 포함
          <HelpTip text="콘텐츠가 많으면 파일이 크고 오래 걸립니다. 이때는 원본을 빼고 받을 수 있습니다." label="콘텐츠 원본 도움말" />
        </label>
        <button
          type="button"
          className="btn btn-primary"
          disabled={action.pending}
          onClick={() => void action.run(() => client.downloadBackup(includeMedia))}
        >
          <Download className="h-3.5 w-3.5" aria-hidden="true" /> 백업 파일 내려받기
        </button>
        <p className="hint">받은 파일은 이 서버가 아닌 곳(다른 PC·외장 디스크·사내 저장소)에 보관하세요.</p>
        {/* 자동 외부 보관 안내는 처음 한 번 읽으면 되는 말이라 물음표 뒤로. */}
        <p className="hint">
          매일 자동으로 다른 디스크에 두려면 <b>백업 외부 보관</b>
          <HelpTip
            text="매일 자동으로 NAS·다른 디스크에 두는 백업 외부 보관은 개발자가 정비 모드 ▸ 플러그인에서 설정합니다. 서버 셸에서 signageonectl backup-external <경로> 로도 켤 수 있습니다."
            label="백업 외부 보관 도움말"
          />
        </p>
      </div>
    </Panel>
  );
}

/** 주소 DB 한 가지(주소·위치)의 한 줄 — 기준일·건수, 없으면 "없음". */
function addressDbLine(summary: AddressDbSummary): string {
  if (!summary.loaded) return '없음';
  return `${summary.base_date ?? '기준일 모름'} · ${summary.count.toLocaleString('ko-KR')}건${summary.source ? ` (${summary.source})` : ''}`;
}

/** 넣는 중인 일 한 줄 — 무엇을 몇 % 읽었나, 끝났으면 결과. */
function addressJobLine(job: NonNullable<AddressDbStatus['job']>): string {
  const what = `${job.dataset === 'address' ? '도로명주소' : '위치정보요약DB'} ${job.mode === 'full' ? '전체분' : '변동분'}`;
  if (job.state === 'running') return `${what} 넣는 중 · ${job.percent ?? 0}% (${(job.rows ?? 0).toLocaleString('ko-KR')}줄)`;
  if (job.state === 'failed') return `${what} 실패 — ${job.error ?? '까닭 모름'}`;
  return `${what} 넣음 · ${(job.count ?? 0).toLocaleString('ko-KR')}건`;
}

/**
 * 주소 DB — 행정안전부 주소정보누리집(business.juso.go.kr)에서 받은 zip 을 올리면 서버 DB 에 넣는다(AssetOne 과 같은
 * 방식, 2026-10-22). 사업장·위치에서 주소를 검색해 고를 때 이 안에서 찾는다 — 인터넷이 없어도 된다. 「도로명주소 한글」
 * 이 있어야 검색되고, 「위치정보요약DB」 까지 넣어야 고른 주소에 좌표가 붙어 모니터링 지도에 선다.
 *
 * 넣기는 서버 뒤에서 몇 분 돈다 — 도는 동안 3초마다 진행을 묻는다. 공개 주소가 Cloudflare 를 지나면 100MB 를 넘는 zip
 * 은 막히므로 사내 주소로 열거나 서버 명령줄(`node dist/cli/import-address-db.js`)로 넣는다.
 */
function AddressDbPanel({ client }: { client: SignageOneClient }): ReactNode {
  const status = useAsync(() => client.addressDbStatus(), [client]);
  const action = useAction();
  const input = useRef<HTMLInputElement>(null);
  const running = status.data?.job?.state === 'running';
  usePolling(() => {
    if (running) status.reload();
  }, 3_000);

  return (
    <Panel
      title="주소 DB"
      subtitle={status.data ? (status.data.loaded ? `주소 ${status.data.count.toLocaleString('ko-KR')}건` : '없음') : undefined}
      help="사업장·위치에서 주소를 검색할 때 쓰는 행정안전부 주소 자료입니다. 주소정보누리집(business.juso.go.kr) ▸ 주소정보 제공에서 「도로명주소 한글」(검색)과 「위치정보요약DB」(지도 좌표) zip 을 받아 하나씩 올립니다. 전체분을 먼저, 그 뒤 변동분을 올립니다."
      collapsible
    >
      <div className="space-y-3 p-4">
        {status.error ? <ErrorNote message={status.error} /> : null}
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}
        {status.data ? (
          <>
            <Row label="도로명주소" value={<span>{addressDbLine(status.data)}</span>} />
            <Row label="위치(좌표)" value={<span>{addressDbLine(status.data.location)}</span>} />
            {status.data.job ? (
              <p className={`text-[12.5px] ${status.data.job.state === 'failed' ? 'text-rose-700' : 'text-slate-600'}`} role="status">
                {addressJobLine(status.data.job)}
              </p>
            ) : null}
          </>
        ) : (
          <p className="hint">확인 중…</p>
        )}
        <input
          ref={input}
          type="file"
          accept=".zip,application/zip"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            void action.run(
              () => client.importAddressDb(file),
              () => status.reload(),
              (result) => `${result.dataset === 'address' ? '도로명주소' : '위치정보요약DB'} ${result.mode === 'full' ? '전체분' : '변동분'}을 넣기 시작했습니다`,
            );
          }}
        />
        <button type="button" className="btn" disabled={action.pending || running} onClick={() => input.current?.click()}>
          <Upload className="h-3.5 w-3.5" aria-hidden="true" /> {action.pending ? '올리는 중…' : 'zip 올리기'}
        </button>
        <p className="hint">
          위치정보요약DB 전체분(약 150MB)처럼 100MB 를 넘는 파일은 외부 주소(Cloudflare)로는 올라가지 않습니다 — 사내 주소로
          콘솔을 열어 올리세요.
        </p>
      </div>
    </Panel>
  );
}
